import { FC } from "react";

import Grid from "@mui/material/Grid";
import { TicketingFormCategory, TicketingRecurrenceType } from "@simplyk/common";
import { DateTime } from "luxon";
import { useFieldArray, useWatch } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";

import { getOnlyUpcomingVisibleRecurrenceId } from "./helpers/getOnlyUpcomingVisibleRecurrenceId";
import { RecurrenceCard } from "./RecurrenceCard/RecurrenceCard";
import { TicketingRecurrence } from "./TicketingRecurrence";
import { EditRecurrencesFormProps, TicketingRecurrenceListProps } from "./types";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { Add } from "@/icons/outlined";
import { Translations } from "@/public/@types/translations";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";

export const MAX_RECURRENCES_COUNT_ALLOWED = 100;

const getRecurrenceListTitleTranslationKey = (category: TicketingFormCategory): keyof Translations["dashboard"] => {
  if (category === TicketingFormCategory.Lottery) {
    return "ticketing.eventDate.lottery";
  }
  if (category === TicketingFormCategory.Auction) {
    return "ticketing.eventDate.auction";
  }
  return "ticketing.eventDate";
};

export const TicketingRecurrenceList: FC<TicketingRecurrenceListProps> = ({
  form,
  ticketing,
  timezone,
  isAuction,
  amplitudePayload,
  recurrencesHasSales,
}) => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const { isOnboarding, editOrCreateMode } = useLiveFormEditorContext<"ticketing">();
  const recurrencesFromData = ticketing.recurrences;
  const isSweepstakes = isSweepstakesCategory(ticketing.formCategory);
  const isLottery = ticketing.formCategory === TicketingFormCategory.Lottery;

  const {
    fields: recurrences,
    append,
    remove,
    update,
  } = useFieldArray<EditRecurrencesFormProps>({
    control: form.control,
    name: "recurrences",
    // this is a hack because useFieldArray generates its own id https://react-hook-form.com/api/usefieldarray/
    keyName: "key" as "id",
  });

  const addRecurrence = () => {
    logAmplitudeEvent(
      AmplitudeEvents.FormEventDateAdded,
      amplitudePayload ?? {
        is_editing: editOrCreateMode === "edit",
        is_onboarding: isOnboarding,
      }
    );

    const startDate = DateTime.now().setZone(timezone).startOf("day").plus({ months: 1, hours: 16 });
    const endDate = startDate.plus({ hours: 4 });

    append([
      {
        id: uuidv4(),
        type: TicketingRecurrenceType.Once,
        startUtc: startDate.toJSDate(),
        endUtc: endDate.toJSDate(),
        closeDate: null,
        openDate: null,
        repeatUntilDate: null,
        isHidden: false,
      },
    ]);
  };

  const deleteRecurrence = (index: number) => {
    remove(index);
  };

  const title = isSweepstakes
    ? t("dashboard", "sweepstakes.date.cardTitle")
    : t("dashboard", getRecurrenceListTitleTranslationKey(ticketing.formCategory));

  const watchedRecurrences = useWatch({ control: form.control, name: "recurrences" });
  const onlyUpcomingVisibleRecurrenceId = getOnlyUpcomingVisibleRecurrenceId(watchedRecurrences, new Date());

  return (
    <Grid container spacing={2}>
      {recurrences.map((recurrence, index) => {
        const liveRecurrence = { ...recurrence, ...watchedRecurrences?.[index], id: recurrence.id };
        const recurrenceFromData = recurrencesFromData?.find((r) => r.id === recurrence.id);
        const isAlreadySaved = Boolean(recurrenceFromData);
        const isHidden = liveRecurrence.isHidden ?? false;
        const hasSales =
          !recurrencesHasSales?.isLoading &&
          (recurrencesHasSales?.data?.find((r) => r.id === recurrence.id)?.hasSales ?? false);

        const cannotHide = hasSales && recurrence.id === onlyUpcomingVisibleRecurrenceId;

        const cardTitle = title + (recurrences.length > 1 ? ` ${index + 1}` : "");
        const isDeletionLocked = (isSweepstakes || isLottery) && recurrences.length === 1;

        const listKey = recurrence.id ?? String(index);
        return (
          <RecurrenceCard
            key={listKey}
            id={listKey}
            header={{ title: cardTitle, isHidden, titleVariant: isAuction ? "body2" : "subtitle1" }}
            actionsLoading={recurrencesHasSales?.isLoading}
            actions={{
              ...(!isAuction &&
                isAlreadySaved && {
                  visibility: {
                    isHidden,
                    disabled: cannotHide,
                    disabledTooltip: cannotHide
                      ? t(
                          "dashboard",
                          isSweepstakes
                            ? "sweepstakes.dates.cannotHideOnlyUpcomingDateWithPayments"
                            : "ticketing.recurrence.cannotHideOnlyUpcomingDateWithPayments"
                        )
                      : undefined,
                    onToggle: () => {
                      logAmplitudeEvent(
                        isHidden ? AmplitudeEvents.EventDateShown : AmplitudeEvents.EventDateHidden,
                        amplitudePayload ?? {
                          is_editing: editOrCreateMode === "edit",
                          is_onboarding: isOnboarding,
                        }
                      );
                      update(index, { ...liveRecurrence, isHidden: !isHidden });
                    },
                  },
                }),
              ...(!isAuction &&
                !isDeletionLocked && {
                  delete: {
                    onDelete: () => deleteRecurrence(index),
                    disabled: hasSales,
                    confirmBefore: false,
                    disabledTooltip: hasSales
                      ? t(
                          "dashboard",
                          cannotHide
                            ? "ticketing.recurrence.cannotDeleteOnlyUpcomingDateWithPayments"
                            : ("ticketing.recurrence.cannotDeleteHasPayments" as keyof Translations["dashboard"])
                        )
                      : undefined,
                  },
                }),
            }}
          >
            <DashboardTooltip title={ticketing.auctionSoldDate && t("dashboard", "ticketingIsSold")}>
              <div style={{ width: "100%" }}>
                <TicketingRecurrence
                  form={form}
                  recurrenceIndex={index}
                  recurrence={recurrence}
                  isAlreadySaved={isAlreadySaved}
                  timezone={timezone}
                  isAuction={isAuction}
                  disabled={Boolean(ticketing.auctionSoldDate)}
                  isHidden={isHidden}
                />
              </div>
            </DashboardTooltip>
          </RecurrenceCard>
        );
      })}
      {!isAuction && recurrences.length < MAX_RECURRENCES_COUNT_ALLOWED && (
        <Grid item xs={12}>
          <Grid container direction="row" alignItems="center">
            <DashboardButton
              variant="outlined"
              vibe="brand"
              onClick={addRecurrence}
              startIcon={<Add />}
              data-test="add-recurrence-button"
            >
              {isSweepstakes
                ? t("dashboard", "sweepstakes.date.addAnother")
                : recurrences.length > 0
                  ? t("dashboard", "ticketing.addAnotherDate")
                  : t("dashboard", "ticketing.addADate")}
            </DashboardButton>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
